import React, { useMemo, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    LinearProgress,
    IconButton,
    Tooltip,
    useTheme,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import BusinessIcon from '@mui/icons-material/Business';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Draggable } from '@hello-pangea/dnd';
import { format, differenceInDays, addDays, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import { Herramienta } from '../../types';

interface HerramientaCardProps {
    herramienta: Herramienta;
    index: number;
    onEdit?: (herramienta: Herramienta) => void;
    onDelete?: (id: string) => void;
}

const HerramientaCard: React.FC<HerramientaCardProps> = ({ herramienta, index, onEdit, onDelete }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event?: React.MouseEvent) => {
        if (event) event.stopPropagation();
        setAnchorEl(null);
    };

    const handleEditClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        handleMenuClose();
        if (onEdit) onEdit(herramienta);
    };

    const handleDeleteClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        handleMenuClose();
        if (onDelete) onDelete(herramienta.id);
    };

    const rentalInfo = useMemo(() => {
        if (herramienta.tipo !== 'alquilada' || !herramienta.fechaInicioAlquiler || !herramienta.duracionAlquilerDias) {
            return null;
        }

        const fechaInicio = herramienta.fechaInicioAlquiler.toDate ? herramienta.fechaInicioAlquiler.toDate() : new Date(herramienta.fechaInicioAlquiler);
        const fechaFin = addDays(fechaInicio, herramienta.duracionAlquilerDias);
        const today = new Date();

        const daysTotal = herramienta.duracionAlquilerDias;
        const daysElapsed = differenceInDays(today, fechaInicio);
        const daysRemaining = differenceInDays(fechaFin, today);

        // Ensure values are within bounds for progress bar
        const progress = Math.min(100, Math.max(0, (daysElapsed / daysTotal) * 100));

        const isOverdue = isPast(fechaFin) && daysRemaining < 0;
        const isNearDue = !isOverdue && daysRemaining <= 2;

        return {
            fechaFin,
            daysRemaining,
            progress,
            isOverdue,
            isNearDue
        };
    }, [herramienta]);

    return (
        <Draggable draggableId={herramienta.id} index={index}>
            {(provided, snapshot) => (
                <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{
                        mb: 2,
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        transform: snapshot.isDragging ? 'rotate(3deg)' : 'none',
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        boxShadow: snapshot.isDragging ? theme.shadows[8] : theme.shadows[1],
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                            boxShadow: theme.shadows[3]
                        },
                        position: 'relative',
                        overflow: 'visible'
                    }}
                >
                    {/* Visual indicator for rented tools */}
                    {herramienta.tipo === 'alquilada' && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: 4,
                                height: '100%',
                                bgcolor: rentalInfo?.isOverdue ? 'error.main' : rentalInfo?.isNearDue ? 'warning.main' : 'info.main',
                                borderTopLeftRadius: 4,
                                borderBottomLeftRadius: 4
                            }}
                        />
                    )}

                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ fontSize: '0.95rem', lineHeight: 1.2, pr: 2 }}>
                                {herramienta.nombre} {herramienta.cantidad && herramienta.cantidad > 1 ? `(x${herramienta.cantidad})` : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {herramienta.tipo === 'alquilada' && (
                                    <Chip
                                        label="Alq."
                                        size="small"
                                        color={rentalInfo?.isOverdue ? "error" : "default"}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                )}
                                {(onEdit || onDelete) && (
                                    <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.25 }}>
                                        <MoreVertIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        {herramienta.descripcion && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {herramienta.descripcion}
                            </Typography>
                        )}

                        {rentalInfo && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" color={rentalInfo.isOverdue ? 'error' : 'text.secondary'}>
                                        {rentalInfo.isOverdue
                                            ? `Vencido hace ${Math.abs(rentalInfo.daysRemaining)} días`
                                            : `Quedan ${rentalInfo.daysRemaining} días`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(rentalInfo.fechaFin, 'd MMM', { locale: es })}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={rentalInfo.progress}
                                    color={rentalInfo.isOverdue ? "error" : rentalInfo.isNearDue ? "warning" : "info"}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        )}

                        {!rentalInfo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Propia
                                </Typography>
                            </Box>
                        )}
                    </CardContent>

                    {/* Menu for tool actions */}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => handleMenuClose()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {onEdit && (
                            <MenuItem onClick={handleEditClick}>
                                <ListItemIcon>
                                    <EditIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Editar" />
                            </MenuItem>
                        )}
                        {onDelete && (
                            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                                <ListItemIcon>
                                    <DeleteOutlineIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                <ListItemText primary="Eliminar" />
                            </MenuItem>
                        )}
                    </Menu>
                </Card>
            )}
        </Draggable>
    );
};

export default HerramientaCard;
